"use client";

import React, { useState, useEffect } from 'react';
import { GiExplosiveMaterials } from "react-icons/gi";
import { MdOutlineTipsAndUpdates, MdOutlinePersonalInjury } from "react-icons/md";
import useFetcherUser from "@/components/checkout/FtecherUser";
import { useSession } from "next-auth/react";
import RedirectToCard from "@/components/login-order/loginRedirectToCard";
import RedirectToCrypto from "@/components/login-order/loginRedirectToCrypto";

import {
  FacebookShareButton,
  PinterestShareButton,
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  VKShareButton,
  EmailShareButton
} from 'next-share'

import { useLocale, useTranslations } from "next-intl";
import { TbAffiliate } from "react-icons/tb";
import { RiTwitterXFill } from "react-icons/ri";
import { FaCreditCard } from "react-icons/fa6";
import { FaBitcoin } from "react-icons/fa6";
import { MdCopyAll } from "react-icons/md";
import { Link } from '@/i18n/routing';
import { FaFacebook } from "react-icons/fa";
import { SiReddit } from "react-icons/si";
import { FaTelegram } from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io";
import { FaPinterest } from "react-icons/fa";
import { FaLinkedinIn } from "react-icons/fa";
import { IoIosCopy } from "react-icons/io";
import AuthPopup from '@/components/login-order/loginOrdinaryPopup';
import ExportPublicFolder from "@/components/login-order/downloadPackHomePage";
import { getPackFiles } from "@/lib/packFiles";
import PaymentCrypto from "@/components/login-order/cryptoOrderHomePAge";
import PaymentCard from "@/components/login-order/cardOrderHomePage";

// ─────────────────────────────────────────────
// Skeleton shimmer for payment buttons area
// ─────────────────────────────────────────────

const ButtonSkeleton = () => (
  <div className="flex flex-col gap-4 pb-4 xl:pb-6 animate-pulse">
    <div className="h-14 w-80 md:w-[28rem] lg:w-[32rem] rounded-xl bg-white/20" />
    <div className="h-14 w-80 md:w-[28rem] lg:w-[32rem] rounded-xl bg-white/15" />
  </div>
);

export default function Ende() {
  const [isCopied, setIsCopied] = useState(false);
  const { data: session, status } = useSession();
  const [domain, setDomain] = useState('');
  const th = useTranslations('Padel-Master-The-Game-Home-Page');
  const l = useLocale();
  const { userinfo } = useFetcherUser();

  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [authCard, setAuthCard] = useState<"login" | "signup">("login");
  const [authCrypto, setAuthCrypto] = useState<"login" | "signup">("login");
  const [isAuthOpenCard, setIsAuthOpenCard] = useState(false);
  const [isAuthOpenCrypto, setIsAuthOpenCrypto] = useState(false);

  // True while session or userinfo hasn't resolved yet
  const isPaymentLoading =
    status === "loading" ||
    (status === "authenticated" && userinfo === undefined);

  const openCard = () => {
    setAuthCard("login");
    setIsAuthOpenCard(true);
  };

  const openCrypto = () => {
    setAuthCrypto("login");
    setIsAuthOpenCrypto(true);
  };

  const openLogin = () => {
    setAuthMode("login");
    setIsAuthOpen(true);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDomain(window.location.origin);
    }
  }, []);

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(domain)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
  };

  const social = () => (
   <div className="flex gap-3 text-secondary flex-wrap">
  <FacebookShareButton url={domain} aria-label="Share on Facebook">
    <FaFacebook size={28} aria-hidden="true" className="hover:text-[#1877F2] text-gray-400" />
  </FacebookShareButton>

  <TwitterShareButton url={domain} aria-label="Share on X">
    <RiTwitterXFill size={28} aria-hidden="true" className="hover:text-gray-600 text-gray-400" />
  </TwitterShareButton>

  <RedditShareButton url={domain} aria-label="Share on Reddit">
    <SiReddit size={28} aria-hidden="true" className="hover:text-[#FF4500] text-gray-400" />
  </RedditShareButton>

  <TelegramShareButton url={domain} aria-label="Share on Telegram">
    <FaTelegram size={28} aria-hidden="true" className="hover:text-blue-500 text-gray-400" />
  </TelegramShareButton>

  <WhatsappShareButton url={domain} aria-label="Share on WhatsApp">
    <IoLogoWhatsapp size={28} aria-hidden="true" className="hover:text-[#25D366] text-gray-400" />
  </WhatsappShareButton>

  <PinterestShareButton url={domain} media={"description"} aria-label="Share on Pinterest">
    <FaPinterest size={28} aria-hidden="true" className="hover:text-[#E60023] text-gray-400" />
  </PinterestShareButton>

  <LinkedinShareButton url={domain} aria-label="Share on LinkedIn">
    <FaLinkedinIn size={28} aria-hidden="true" className="hover:text-[#0077B5] text-gray-400" />
  </LinkedinShareButton>

  {isCopied ? (
    <MdCopyAll size={28} aria-hidden="true" className="text-green-500" role="img" aria-label="Copied" />
  ) : (
    <IoIosCopy
      size={28}
      onClick={handleCopyPassword}
      role="button"
      tabIndex={0}
      aria-label="Copy link"
      onKeyDown={(e) => e.key === "Enter" && handleCopyPassword()}
      className="hover:text-green-500 text-gray-400 cursor-pointer"
    />
  )}
</div>
  );

  // ── Render the correct payment UI based on loading/auth/payment state ──
  const renderPaymentButtons = () => {
    // 1. Still loading — show skeleton
    if (isPaymentLoading) return <ButtonSkeleton />;

    // 2. Authenticated
    if (status === "authenticated") {
      // 2a. Already paid — show download
      if (userinfo?.is_payed) {
        return (
          <div>
            <p className="text-b text-sm pb-2 capitalize max-w-md">
              {th("download-button-guide")}
            </p>
            <ExportPublicFolder
              locale={l || "en"}
              files={getPackFiles(l || "en")}
              folderName={`pack_${l || "en"}`}
            />
          </div>
        );
      }

      // 2b. Authenticated but not paid — show payment buttons
      return (
        <div className="flex flex-col items-center justify-center gap-4 pb-4 xl:pb-6">
          <PaymentCard
            customData={{ productType: "ebook", source: "homepage" }}
            className="text-center text-secondary flex justify-center gap-1 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
          >
            {th("buy-card-button")}
          </PaymentCard>
          <PaymentCrypto />
        </div>
      );
    }

    // 3. Not authenticated — show login-gated buy buttons
    return (
      <div className="flex flex-col items-center justify-center gap-4 pb-4 xl:pb-6">
        <button
          className="text-center text-secondary flex justify-center gap-2 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
          onClick={openCard}
        >
          <FaCreditCard size={24} />
          <p className="m-0 text-xs md:text-sm lg:text-base font-bold">{th("buy-card-button")}</p>
        </button>
        <button
          className="text-center text-secondary flex justify-center gap-1 md:gap-3 font-medium w-80 md:w-[28rem] lg:w-[32rem] hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
          onClick={openCrypto}
        >
          <FaBitcoin size={26} />
          <p className="m-0 text-xs md:text-sm lg:text-base font-bold">{th("buy-crypto-button")}</p>
        </button>
      </div>
    );
  };

  // ── Title also depends on payment state — skeleton for it too ──
  const renderTitle = () => {
    if (isPaymentLoading) {
      return (
        <div className="animate-pulse space-y-3">
          <div className="h-8 w-72 rounded-lg bg-white/20" />
          <div className="h-6 w-56 rounded-lg bg-white/15" />
        </div>
      );
    }

    if (!userinfo?.is_payed) {
      return (
        <div className="space-y-4">
          <h2 className="text-xl xl:text-2xl 2xl:text-3xl font-extrabold text-white uppercase tracking-wide">
            {th("title6")}
          </h2>
          <p className="text-xl xl:text-2xl uppercase text-white">{th("sub-title6")}</p>
        </div>
      );
    }

    return (
      <div className="pb-4">
        <h2 className="text-xl xl:text-2xl 2xl:text-3xl font-extrabold text-white uppercase tracking-wide">
          {th("title7")}
        </h2>
      </div>
    );
  };

  return (
    <>
      <div className="bg-a text-white py-20 px-6 lg:px-24 select-none">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-4 gap-8">

          {/* Col 1–2: Price + Payment */}
          <div className="col-span-1 md:col-span-2 space-y-4">
            {renderTitle()}
            {renderPaymentButtons()}
          </div>

          {/* Col 3: Affiliate */}
          <div className="flex flex-col gap-6 justify-center">
            <p>
              {th("affiliat-text1")} <span className="font-semibold text-lg">30%</span>{" "}{th("affiliat-text2")}
            </p>

            {status === "authenticated" ? (
              <Link href="/account" rel="preload">
                <button className="text-center w-fit text-b flex gap-1 font-medium text-xs md:text-sm hover:bg-a hover:text-white border-1 border-a rounded-xl px-8 md:px-18 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-secondary backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring">
                  <TbAffiliate className="inline-block mr-3 font-extrabold" size={22} />
                  {th("affiliat-button")}
                </button>
              </Link>
            ) : (
              <button
                onClick={openLogin}
                className="text-center text-b w-fit flex gap-1 font-medium text-xs md:text-sm hover:bg-a hover:text-white border-1 border-a rounded-xl px-8 md:px-18 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-secondary backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring"
              >
                <TbAffiliate className="inline-block mr-3 font-extrabold" size={22} />
                {th("affiliat-button")}
              </button>
            )}
          </div>

          {/* Col 4: Social */}
          <div className="flex flex-col gap-4">
            <h3 className="text-white">{th("social-share")}</h3>
            {social()}
          </div>

        </div>
      </div>

      <hr className="w-full border-t border-white" />

      <AuthPopup
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        initialMode={authMode}
      />
      <RedirectToCard
        isOpen={isAuthOpenCard}
        onClose={() => setIsAuthOpenCard(false)}
        initialMode={authCard}
      />
      <RedirectToCrypto
        isOpen={isAuthOpenCrypto}
        onClose={() => setIsAuthOpenCrypto(false)}
        initialMode={authCrypto}
      />
    </>
  );
}